/**
 * Host for the post preview popup.
 *
 * Any mode can open it with `openPreview(postId, ids)`; `ids` is the caller's current list in its
 * current filtered/sorted order, which is what prev/next walks (PRD: "prev/next navigation steps
 * through the current filtered list").
 *
 * **Which post is open lives in the URL** (`?post=<id>`, §12.1) so every popup is deep-linkable:
 * opening pushes a history entry (Back closes), prev/next and in-dialog hops replace it (one Back
 * still closes), and closing pops that entry — or deletes the param when the popup came from a
 * pasted link, which has no entry of ours to pop. `ids` stays component state: it is the caller's
 * live list, not something a URL can carry, so a cold deep link opens standalone (no prev/next).
 */

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import { useViewRoute } from '../../app/useViewRoute';
import { PostPreviewDialog } from './PostPreviewDialog';

type PreviewApi = {
  openPreview: (postId: number, ids: number[]) => void;
};

const PreviewContext = createContext<PreviewApi | null>(null);

export function usePostPreview(): PreviewApi {
  const api = useContext(PreviewContext);
  if (api === null) throw new Error('usePostPreview must be used inside <PostPreviewProvider>');
  return api;
}

export type PostPreviewProviderProps = {
  mediaEnabled: boolean;
  timezone: string;
  children: ReactNode;
};

export function PostPreviewProvider({ mediaEnabled, timezone, children }: PostPreviewProviderProps) {
  const { previewPostId, setPreviewPostId, historyBack } = useViewRoute();
  const [ids, setIds] = useState<number[]>([]);
  // Did *we* push the entry? False on a pasted deep link or a reload, where there is nothing to pop.
  const pushedRef = useRef(false);

  const openPreview = useCallback(
    (postId: number, list: number[]) => {
      setIds(list.includes(postId) ? list : [postId, ...list]);
      pushedRef.current = true;
      setPreviewPostId(postId, { push: true });
    },
    [setPreviewPostId],
  );

  // Covers browser Back as well as our own close: once the param is gone, so is our entry.
  useEffect(() => {
    if (previewPostId === null) pushedRef.current = false;
  }, [previewPostId]);

  const api = useMemo<PreviewApi>(() => ({ openPreview }), [openPreview]);

  return (
    <PreviewContext.Provider value={api}>
      {children}
      {previewPostId !== null ? (
        <PostPreviewDialog
          postId={previewPostId}
          // Empty on a cold deep link, and a post opened from outside the list that spawned the
          // dialog (the embedded original of a repost, which need not be in the current filter)
          // is simply absent from it: either way the dialog stands alone rather than sitting at
          // position -1 with dead nav buttons.
          ids={ids}
          mediaEnabled={mediaEnabled}
          timezone={timezone}
          // Hops replace, so any number of them still cost exactly one Back to close.
          onNavigate={(postId) => setPreviewPostId(postId)}
          onClose={() => (pushedRef.current ? historyBack() : setPreviewPostId(null))}
        />
      ) : null}
    </PreviewContext.Provider>
  );
}
